/**
 * Icon and readable label for a template node's `kind`
 * (`ProposalNode`/`ChangeNode`), shared by `ProposalView` and `ChangeView`.
 * "Position" and "Product" reuse the existing `Global` labels (the same
 * words are used as a data-grid column header and a product box title);
 * "Section" and "Decision" reuse the existing `ItemTree` labels (the same
 * item-kind vocabulary used when adding items to a document tree); every
 * other kind gets its own label in the `Assistant` namespace.
 */
import {
  AccountTree as SectionIcon,
  CallSplit as DecisionIcon,
  Category as TypeIcon,
  Extension as BlockIcon,
  Inventory2 as ProductIcon,
  ReceiptLong as PositionIcon,
} from "@mui/icons-material";
import { useFormatting } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type {
  ChangeQuestionValue,
  NodeDetail,
  TemplateNodeKind,
} from "./templateViewTypes.js";

export const TEMPLATE_NODE_ICON: Record<TemplateNodeKind, React.ReactNode> = {
  section: <SectionIcon fontSize="small" />,
  decision: <DecisionIcon fontSize="small" color="secondary" />,
  position: <PositionIcon fontSize="small" />,
  product: <ProductIcon fontSize="small" />,
  product_placeholder: <ProductIcon fontSize="small" color="disabled" />,
  template: <BlockIcon fontSize="small" color="primary" />,
  template_type: <TypeIcon fontSize="small" color="primary" />,
};

export function useTemplateNodeKindLabel() {
  const { t } = useTranslate(["Assistant", "Global", "ItemTree"]);
  return (kind: TemplateNodeKind): string => {
    switch (kind) {
      case "section":
        return t("Section", { ns: "ItemTree" });
      case "decision":
        return t("Decision", { ns: "ItemTree" });
      case "position":
        return t("Position", { ns: "Global" });
      case "product":
        return t("Product", { ns: "Global" });
      case "product_placeholder":
        return t("Product placeholder", { ns: "Assistant" });
      case "template":
        return t("Building block", { ns: "Assistant" });
      case "template_type":
        return t("Type placeholder", { ns: "Assistant" });
    }
  };
}

/**
 * Formats one structured `NodeDetail` (task 8.6/8.7's locale-agnostic
 * replacement for a pre-formatted string) into display text: numbers and
 * currency through the user's locale (never a hardcoded `de-DE`), and a
 * marker kind (`unpaid`, `freeText`, `decisionBehavior`, ...) through Tolgee.
 * Join several with " · " for one node's/field's full detail line.
 */
export function useNodeDetailFormatter() {
  const { t } = useTranslate(["Assistant", "Decisions"]);
  const { getFormattedPrice, getFormattedFloat, getFormattedDuration } =
    useFormatting();
  return (detail: NodeDetail): string => {
    switch (detail.kind) {
      case "text":
        return detail.value;
      case "quantity":
        return `${getFormattedFloat(detail.quantity)} ${detail.unit}`.trim();
      case "price":
        return getFormattedPrice(detail.amount) ?? "";
      case "duration":
        return getFormattedDuration(detail.minutes) ?? "";
      case "rate":
        return t("{price}/h", {
          ns: "Assistant",
          price: getFormattedPrice(detail.amount) ?? "",
        });
      case "unpaid":
        return t("Not calculated", { ns: "Assistant" });
      case "decisionBehavior":
        switch (detail.behavior) {
          case "SELECT_ONE":
            return t("SELECT_ONE", { ns: "Decisions" });
          case "SELECT_MANY":
            return t("SELECT_MANY", { ns: "Decisions" });
        }
      case "articleNumber":
        return detail.value;
      case "reference":
        return detail.refKind === "template"
          ? `${t("Building block", { ns: "Assistant" })}: ${detail.label}`
          : `${t("Product placeholder", { ns: "Assistant" })}: ${detail.label}`;
      case "templateTypePlaceholder":
        return t("Type placeholder", { ns: "Assistant" });
      case "options":
        return detail.values.map(String).join(", ");
      case "freeText":
        return t("Free text", { ns: "Assistant" });
    }
  };
}

/** Joins several `NodeDetail`s (or a single plain string field value) for display. */
export function useNodeDetailListFormatter() {
  const formatDetail = useNodeDetailFormatter();
  return (details: NodeDetail[]): string =>
    details.map(formatDetail).join(" · ");
}

/** Formats a `ChangeNode.fieldChanges[]` before/after value, which is either plain text or a `NodeDetail`. */
export function useFieldValueFormatter() {
  const formatDetail = useNodeDetailFormatter();
  return (value: string | NodeDetail): string =>
    typeof value === "string" ? value : formatDetail(value);
}

/**
 * Translates a `ChangeNode.fieldChanges[].field` key (task 10.4: a stable,
 * locale-agnostic identifier — `"title"`, `"description"`, `"quantity"`,
 * `"unitPrice"`, `"estimatedTime"`, `"workSellingRate"`) into display text.
 * `"title"` and `"description"` reuse `Global`'s existing labels; an
 * unrecognised field falls back to the raw key.
 */
export function useChangeFieldLabel() {
  const { t } = useTranslate(["Assistant", "Global"]);
  return (field: string): string => {
    switch (field) {
      case "title":
        return t("Title", { ns: "Global" });
      case "description":
        return t("Description", { ns: "Global" });
      case "quantity":
        return t("Quantity", { ns: "Assistant" });
      case "unitPrice":
        return t("Unit price", { ns: "Assistant" });
      case "estimatedTime":
        return t("Work time", { ns: "Assistant" });
      case "workSellingRate":
        return t("Hourly rate", { ns: "Assistant" });
      default:
        return field;
    }
  };
}

/**
 * Formats a `ChangeNode.questionChanges[].before`/`after` value (task 10.5's
 * structured `{ label, askIf? }`, replacing a pre-joined German string): the
 * question's label, plus a translated "only if {condition}" suffix when its
 * `askIf` is set (and not `"true"`).
 */
export function useChangeQuestionValueFormatter() {
  const { t } = useTranslate(["Assistant"]);
  return (value: ChangeQuestionValue | null): string => {
    if (!value) return "";
    if (!value.askIf || value.askIf === "true") return value.label;
    return `${value.label} · ${t("only if {condition}", { ns: "Assistant", condition: value.askIf })}`;
  };
}
